import { Button, Card, Col, Modal, Row, Skeleton, Statistic, Typography } from "antd";
import type { EChartsOption } from "echarts";
import ReactECharts from "echarts-for-react";
import type { Currency } from "stores/localeStore";
import { formatCurrencyAmount } from "utility/formatCurrency";
import { useRevenueDailyTimeseriesQuery } from "../hooks/useRevenueDailyTimeseriesQuery";
import type { RevenueMarketFilter, RevenueTimeseriesPoint } from "../types";

const { Text } = Typography;

const VB_BLUE = "#195178";
const LINE_COLOR = "#faad14";

const MONTH_LABELS_SV = [
  "jan",
  "feb",
  "mar",
  "apr",
  "maj",
  "jun",
  "jul",
  "aug",
  "sep",
  "okt",
  "nov",
  "dec",
] as const;

const fmtSv = (value: number) => new Intl.NumberFormat("sv-SE").format(value);

function formatRevenue(value: number, currency: RevenueMarketFilter): string {
  if (currency) {
    return formatCurrencyAmount(value, currency as Currency);
  }
  return `${fmtSv(Math.round(value))} kr`;
}

function buildDailyOption(
  points: RevenueTimeseriesPoint[],
  currency: RevenueMarketFilter,
): EChartsOption {
  const categories = points.map((p) => String(Number(p.period.split("-")[2])));
  const revenue = points.map((p) => p.revenue);
  const orderCounts = points.map((p) => p.orderCount);

  return {
    grid: { left: 8, right: 8, top: 30, bottom: 4, containLabel: true },
    tooltip: {
      trigger: "axis",
      axisPointer: { type: "shadow" },
      formatter: (params) => {
        const items = Array.isArray(params) ? params : [params];
        const first = items[0] as { name?: string; axisValue?: string | number } | undefined;
        const day = String(first?.axisValue ?? first?.name ?? "");
        const lines = items.map((item) => {
          const raw = Number(item.value ?? 0);
          const formatted =
            item.seriesName === "Omsättning"
              ? formatRevenue(raw, currency)
              : fmtSv(raw);
          return `${item.marker ?? ""}${item.seriesName}: ${formatted}`;
        });
        return [`Dag ${day}`, ...lines].join("<br/>");
      },
    },
    legend: {
      data: ["Omsättning", "Ordrar"],
      right: 0,
      top: 0,
      icon: "roundRect",
      itemWidth: 10,
      itemHeight: 10,
      textStyle: { color: "rgba(0,0,0,0.65)" },
    },
    xAxis: {
      type: "category",
      data: categories,
      axisLine: { lineStyle: { color: "#f0f0f0" } },
      axisTick: { show: false },
      axisLabel: { color: "rgba(0,0,0,0.45)" },
    },
    yAxis: [
      {
        type: "value",
        splitLine: { lineStyle: { color: "#f5f5f5" } },
        axisLabel: {
          color: "rgba(0,0,0,0.45)",
          formatter: (value: number) => fmtSv(value),
        },
      },
      {
        type: "value",
        splitLine: { show: false },
        axisLabel: { color: "rgba(0,0,0,0.45)", formatter: fmtSv },
      },
    ],
    series: [
      {
        name: "Omsättning",
        type: "bar",
        data: revenue,
        barMaxWidth: 18,
        itemStyle: {
          color: VB_BLUE,
          borderRadius: [3, 3, 0, 0],
        },
      },
      {
        name: "Ordrar",
        type: "line",
        yAxisIndex: 1,
        data: orderCounts,
        smooth: true,
        showSymbol: false,
        lineStyle: { width: 2, color: LINE_COLOR },
        itemStyle: { color: LINE_COLOR },
      },
    ],
  };
}

export type RevenueDailyModalProps = {
  open: boolean;
  period: string | null;
  market: RevenueMarketFilter;
  onClose: () => void;
};

export function RevenueDailyModal({
  open,
  period,
  market,
  onClose,
}: RevenueDailyModalProps) {
  const [yearStr, monthStr] = period?.split("-") ?? [];
  const year = yearStr ? Number(yearStr) : null;
  const month = monthStr ? Number(monthStr) : null;
  const monthLabel =
    month && month >= 1 && month <= 12 ? MONTH_LABELS_SV[month - 1] : period ?? "";

  const daily = useRevenueDailyTimeseriesQuery(
    open ? year : null,
    open ? month : null,
    market,
  );

  const points = daily.data?.points ?? [];
  const totalRevenue = points.reduce((sum, p) => sum + p.revenue, 0);
  const totalOrders = points.reduce((sum, p) => sum + p.orderCount, 0);
  const avgPerDay = points.length > 0 ? totalRevenue / points.length : 0;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={920}
      destroyOnClose
      title={
        <div>
          <div style={{ fontWeight: 600, fontSize: 18 }}>
            Omsättning – {monthLabel}
          </div>
          <Text type="secondary" style={{ fontSize: 13, fontWeight: 400 }}>
            Dagsvy, ordrar och omsättning per dag
            {market ? ` · ${market}` : " · alla marknader"}
          </Text>
        </div>
      }
      footer={[
        <Button key="close" onClick={onClose}>
          Stäng
        </Button>,
      ]}
    >
      {daily.isError && (
        <Text type="danger">
          {daily.error instanceof Error
            ? daily.error.message
            : "Kunde inte hämta dagsdata"}
        </Text>
      )}

      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={8}>
          <Card bordered size="small" bodyStyle={{ padding: "12px 16px" }}>
            <Statistic
              title="Omsättning"
              value={totalRevenue}
              loading={daily.isLoading}
              formatter={() => formatRevenue(totalRevenue, market)}
              valueStyle={{ fontSize: 22, fontWeight: 600 }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card bordered size="small" bodyStyle={{ padding: "12px 16px" }}>
            <Statistic
              title="Ordrar"
              value={totalOrders}
              loading={daily.isLoading}
              groupSeparator=" "
              valueStyle={{ fontSize: 22, fontWeight: 600 }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card bordered size="small" bodyStyle={{ padding: "12px 16px" }}>
            <Statistic
              title="Snitt per dag"
              value={avgPerDay}
              loading={daily.isLoading}
              formatter={() => formatRevenue(avgPerDay, market)}
              valueStyle={{ fontSize: 22, fontWeight: 600 }}
            />
          </Card>
        </Col>
      </Row>

      {daily.isLoading ? (
        <Skeleton active paragraph={{ rows: 8 }} title={false} />
      ) : (
        <ReactECharts
          option={buildDailyOption(points, market)}
          style={{ height: 320, width: "100%" }}
          opts={{ renderer: "canvas" }}
          notMerge
        />
      )}
    </Modal>
  );
}
